import { useCallback, useEffect, useState } from 'react'
import { getVapidKey, registerPushSubscription, unregisterPushSubscription } from '../api/subscriptions'

export type PushState = 'loading' | 'unsupported' | 'denied' | 'unsubscribed' | 'subscribed'

const STORAGE_KEY = 'cap-push-subscription-id'

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>('loading')

  useEffect(() => {
    if (!('PushManager' in window) || !('Notification' in window)) {
      setState('unsupported'); return
    }
    if (Notification.permission === 'denied') {
      setState('denied'); return
    }
    setState(localStorage.getItem(STORAGE_KEY) ? 'subscribed' : 'unsubscribed')
  }, [])

  // lat/lon are passed in by the caller — either from browser geolocation or
  // a state capital chosen by the user. null means no location filter.
  const subscribe = useCallback(async (lat: number | null, lon: number | null) => {
    const vapidKey = await getVapidKey()
    if (!vapidKey) throw new Error('VAPID key not configured on server')

    const reg = await navigator.serviceWorker.ready
    const pushSub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
    })

    const json = pushSub.toJSON()
    const id = await registerPushSubscription({
      subscription: {
        endpoint: json.endpoint!,
        expirationTime: json.expirationTime ?? null,
        keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
      },
      min_priority: 3,
      hazard_types: null,
      lat,
      lon,
    })

    localStorage.setItem(STORAGE_KEY, id)
    setState('subscribed')
  }, [])

  const unsubscribe = useCallback(async () => {
    const id = localStorage.getItem(STORAGE_KEY)
    if (id) await unregisterPushSubscription(id)
    const reg = await navigator.serviceWorker.ready
    const pushSub = await reg.pushManager.getSubscription()
    if (pushSub) await pushSub.unsubscribe()
    localStorage.removeItem(STORAGE_KEY)
    setState('unsubscribed')
  }, [])

  return { state, subscribe, unsubscribe }
}
